/**
 * Fila de revisão de identidade (D09) — o degrau 6 da §5.2, pela API.
 *
 * ── O que a escada deixa aqui, e por quê ──────────────────────────────────
 *
 * Os degraus 1 a 4 resolvem por igualdade (vínculo, telefone, e-mail,
 * documento). O 5 calcula um score. Acima do limiar da revenda, mescla
 * sozinho; **abaixo, nunca mescla** — cria o cliente novo e registra a
 * suspeita. Esta é a fila dessas suspeitas.
 *
 * ── O sobrevivente é escolhido por quem revisa, sempre ────────────────────
 *
 * A Armadilha da task: *"aprovar com sobrevivente escolhido pelo sistema. O
 * revisor não percebe qual ficou, e o cliente com o histórico mais rico pode
 * virar o mesclado."*
 *
 * Por isso `sobrevivente_id` é obrigatório no corpo e precisa ser um dos dois
 * candidatos. Não há padrão — nem "o mais antigo", nem "o de maior score".
 * Um padrão aqui seria a armadilha com outro nome: o revisor apertaria
 * "aprovar" sem olhar, e descobriria a escolha errada quando o histórico
 * sumisse.
 *
 * ── Por que o detalhe traz os dois lados inteiros ─────────────────────────
 *
 * A Técnica: *"comparação lado a lado com o dado dos dois candidatos [...] a
 * decisão depende do contexto, não do score"*. E: *"revisor que vê só um score
 * decide no chute"*. Então a lista já traz a contribuição de CADA sinal, e o
 * detalhe traz o que cada cliente tem de conversa, oportunidade e vínculo — o
 * suficiente para decidir qual deles é o registro principal.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';

/**
 * Quanto cada sinal contribuiu para o score (C11).
 *
 * `peso` é fixo por sinal e `similaridade` é o que foi medido; a
 * `contribuicao` é o produto. Os três aparecem porque explicam coisas
 * diferentes: um sinal com similaridade alta e peso baixo não é o mesmo que um
 * com peso alto e similaridade média, e só a contribuição não distingue.
 */
/** Os que existem hoje. A lista CRESCE — ver o campo `sinal`. */
export const SINAIS_CONHECIDOS = [
  'nome',
  'empresa',
  'veiculo_interesse',
  'origem',
  /** Degrau 4: os dois cadastros têm o mesmo CPF ou CNPJ (D23). */
  'documento',
] as const;

export const ContribuicaoDeSinalSchema = z.object({
  /**
   * Texto livre, e não enum fechado.
   *
   * O vocabulário cresce: a C11 tinha quatro sinais pesados, a D23
   * acrescentou `documento`, e cada degrau novo da escada pode acrescentar
   * outro. Num enum fechado, cada acréscimo vira quebra de `/v1` — ADR e
   * `/v2` convivendo para uma palavra numa lista de explicação.
   *
   * E o cliente nunca precisou da exaustividade: a tela já renderiza
   * `NOMES_DE_SINAL[sinal] ?? sinal`, ou seja, foi escrita desde o começo para
   * mostrar o nome cru quando não reconhece. O enum prometia mais do que
   * alguém consumia.
   */
  sinal: z.string(),
  peso: z.number(),
  similaridade: z.number(),
  contribuicao: z.number(),
});

/** O resumo de um cliente, o bastante para triar sem abrir o detalhe. */
export const LadoDoCandidatoSchema = z.object({
  cliente_id: IdSchema,
  nome_exibicao: z.string().nullable(),
  /**
   * Mascarados, como em toda lista (§18).
   *
   * A fila é tela de gestão e mostra DOIS clientes por linha; telefone inteiro
   * aqui dobraria a exposição de contato a cada item, para uma decisão que o
   * final do número já apoia.
   */
  telefone_mascarado: z.string().nullable(),
  email_mascarado: z.string().nullable(),
  criado_em: InstanteSchema,
});

/**
 * Os quatro estados de um candidato.
 *
 * `merge_candidate` é o pendente — o nome vem da migração 015 e descreve o que
 * a linha É, não o que falta nela.
 */
export const StatusDoCandidatoSchema = z.enum([
  'merge_candidate',
  'aprovado',
  'rejeitado',
  'expirado',
]);

export const CandidatoDeMergeSchema = z.object({
  id: IdSchema,
  score: z.number(),
  sinais: z.array(ContribuicaoDeSinalSchema),
  /** Sempre dois, e na ordem em que a escada os registrou. */
  lados: z.array(LadoDoCandidatoSchema),
  criado_em: InstanteSchema,
  status: StatusDoCandidatoSchema,
  /**
   * O desfecho, quando já houve um.
   *
   * A H12 pede "histórico de decisões", e histórico sem o porquê é uma lista de
   * datas: é o `motivo` que permite a quem calibra o limiar entender o padrão
   * das rejeições.
   */
  decidido_em: InstanteSchema.nullable(),
  motivo: z.string().nullable(),
});

export const PaginaDeCandidatosSchema = z.object({
  itens: z.array(CandidatoDeMergeSchema),
  pagina: z.object({
    cursor_proximo: z.string().nullable(),
    tem_mais: z.boolean(),
  }),
  /**
   * O limiar configurado pela revenda, junto da lista.
   *
   * Vai aqui porque o score sozinho não diz nada: 0,72 é alto ou baixo
   * conforme o limiar seja 0,85 ou 0,60. Sem ele, o revisor compara o número
   * com uma régua que ele não tem.
   */
  limiar: z.number(),
});

/* ── Detalhe ─────────────────────────────────────────────────────────────── */

/** O contexto que decide: o que cada cliente acumulou. */
export const ContextoDoLadoSchema = LadoDoCandidatoSchema.extend({
  conversas: z.number().int(),
  oportunidades: z.number().int(),
  /** Vínculos com sistemas de origem (§5.3) — quantos e de onde. */
  vinculos: z.array(
    z.object({
      source_system: z.string(),
      source_object: z.string(),
      method: z.string(),
      criado_em: InstanteSchema,
    }),
  ),
  /** Quando o cliente teve a última interação registrada. Nulo = nenhuma. */
  ultima_interacao_em: InstanteSchema.nullable(),
  /**
   * A clínica (#811): se o cliente tem ficha do paciente, e quantos convênios.
   * Na unificação os dois passam para o que fica, e com duas fichas vale a do
   * que fica: a tela avisa antes de aprovar.
   */
  tem_ficha_do_paciente: z.boolean(),
  convenios: z.number().int(),
  /** Os documentos do paciente (#811), que também passam para o que fica. */
  documentos_do_paciente: z.number().int(),
  /** Os pedidos de exame do paciente, que a unificação passa para o que fica. */
  exames_do_paciente: z.number().int(),
});

export const DetalheDoCandidatoSchema = z.object({
  id: IdSchema,
  score: z.number(),
  limiar: z.number(),
  sinais: z.array(ContribuicaoDeSinalSchema),
  lados: z.array(ContextoDoLadoSchema),
  criado_em: InstanteSchema,
});

/* ── Decisões ────────────────────────────────────────────────────────────── */

export const AprovarMergeSchema = z
  .object({
    /**
     * Qual dos dois SOBREVIVE. Obrigatório, e sem padrão.
     *
     * É a armadilha da task, e a razão de não haver `.default()` aqui: o
     * cliente que sobrevive fica com o histórico dos dois, e o outro vira
     * alias. Escolher errado é o que gera split depois.
     */
    sobrevivente_id: IdSchema,
    motivo: z.string().min(1).max(500).optional(),
  })
  .strict();

export const RejeitarMergeSchema = z
  .object({
    /**
     * Obrigatório, ao contrário do motivo da aprovação.
     *
     * Rejeitar diz que o sistema errou, e a calibragem do limiar depende de
     * saber POR QUE — "são irmãos com o mesmo sobrenome" e "é a mesma pessoa,
     * mas prefiro não mesclar agora" pedem ajustes opostos. Sem o motivo, a
     * fila vira uma lista de nãos sem informação.
     */
    motivo: z.string().min(1).max(500),
  })
  .strict();

export type AprovarMerge = z.infer<typeof AprovarMergeSchema>;
export type RejeitarMerge = z.infer<typeof RejeitarMergeSchema>;

export const DecisaoDoCandidatoSchema = z.object({
  id: IdSchema,
  status: z.enum(['aprovado', 'rejeitado']),
  /** Presente na aprovação: quem ficou. Nulo na rejeição, que não mescla nada. */
  sobrevivente_id: IdSchema.nullable(),
  decidido_em: InstanteSchema,
});

/* ── Métrica ─────────────────────────────────────────────────────────────── */

/**
 * A taxa de vínculo automático (§14.2, meta ≥ 95%).
 *
 * ── O denominador é a parte que importa ───────────────────────────────────
 *
 * Taxa = vínculos que a escada resolveu sozinha ÷ (esses mais os candidatos
 * que ela mandou para revisão). Cada candidato representa uma resolução que
 * NÃO foi automática, e é isso que o denominador precisa contar.
 *
 * A tentação é dividir por "total de vínculos", o que ignoraria a fila
 * inteira: uma revenda com mil ambiguidades pendentes mostraria 100% de
 * automação enquanto ninguém dá conta de revisar.
 *
 * ── Para que ela serve ────────────────────────────────────────────────────
 *
 * A Técnica: *"é o que diz se o limiar está calibrado: fila crescendo
 * significa limiar alto demais; merge indevido, baixo demais"*. Por isso vêm
 * juntos a taxa, o tamanho atual da fila e quantos foram rejeitados — merge
 * indevido aparece como rejeição, e rejeição subindo é o sinal do outro lado.
 */
export const MetricaDeIdentidadeSchema = z.object({
  periodo: z.object({ de: z.string(), ate: z.string() }),
  vinculos_automaticos: z.number().int(),
  candidatos_criados: z.number().int(),
  /** Nulo quando não houve resolução nenhuma no período — não é zero. */
  taxa_automatica: z.number().nullable(),
  /** A meta da §14.2, junto, para a tela não precisar saber o número. */
  meta: z.number(),
  /** Fila de AGORA, não do período: é ela que diz se alguém está dando conta. */
  pendentes: z.number().int(),
  aprovados: z.number().int(),
  rejeitados: z.number().int(),
  expirados: z.number().int(),
});

export type CandidatoDeMerge = z.infer<typeof CandidatoDeMergeSchema>;
export type StatusDoCandidato = z.infer<typeof StatusDoCandidatoSchema>;
export type PaginaDeCandidatos = z.infer<typeof PaginaDeCandidatosSchema>;
export type DetalheDoCandidato = z.infer<typeof DetalheDoCandidatoSchema>;
export type DecisaoDoCandidato = z.infer<typeof DecisaoDoCandidatoSchema>;
export type MetricaDeIdentidade = z.infer<typeof MetricaDeIdentidadeSchema>;
